import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import {
    ArrowRight, Check, Sparkles, Wallet, Clock, AlertTriangle, FileWarning, Quote,
    BarChart3, FileStack, ShieldCheck, GanttChartSquare, HandCoins,
    Building2, Landmark, CalendarCheck, Rocket, HeartHandshake,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLang } from '@/context/LangContext';
import LangSwitcher from '@/components/LangSwitcher';
import Footer from '@/components/Footer';
import PWAInstallBanner from '@/components/PWAInstallBanner';
import TrustedLogosMarquee from '@/components/TrustedLogosMarquee';
import { SECTORS_ALL } from '@/lib/sectors';
import { CURRENCIES, formatPrice } from '@/lib/currency';
import { PLAN_FALLBACK } from '@/lib/plans';
import { LANDING_I18N } from '@/lib/landingI18n';

// Brand visuals — local /public/images/
const IMG_HERO = '/brand/landing-hero.png';
const IMG_ATRIUM = '/images/af-atrium.jpg';
const IMG_WORKSPACE = '/images/af-workspace.jpg';
const IMG_INSPIRER = '/images/af-inspirer.jpg';
const IMG_AI = '/images/af-ia.jpg';

const BENEFIT_ICONS = [CalendarCheck, Wallet, FileStack, ShieldCheck, BarChart3, Sparkles];
const AUDIENCE_ICONS = [Rocket, HeartHandshake, Building2, Landmark];
const AUDIENCE_TO = ['/signup-free', '/p/tarifs', '/p/tarifs', '/p/contact'];
const PAIN_ICONS = [FileWarning, AlertTriangle, Wallet, Clock];
const SOLUTION_ICONS = [GanttChartSquare, BarChart3, FileStack, Sparkles, ShieldCheck, HandCoins];

export default function Landing() {
    const { lang } = useLang();
    const L = LANDING_I18N[lang] || LANDING_I18N.fr;
    const [currency, setCurrency] = useState('EUR');
    const [rexLead, setRexLead] = useState({ name: '', email: '' });
    const [rexBusy, setRexBusy] = useState(false);
    const [rexDone, setRexDone] = useState(false);
    const [rexErr, setRexErr] = useState(false);

    const submitRexLead = async (e) => {
        e.preventDefault();
        if (!rexLead.name.trim() || !rexLead.email.trim()) return;
        setRexBusy(true); setRexErr(false);
        try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/access-requests/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    full_name: rexLead.name.trim(),
                    email: rexLead.email.trim(),
                    source: 'rex-template',
                    message: 'Téléchargement du modèle REX (lead magnet landing)',
                }),
            });
            if (!r.ok) throw new Error('lead failed');
            setRexDone(true);
            trackEvent('rex_template_lead');
            window.open('/docs/Modele-REX-African-Valley.pdf', '_blank');
        } catch {
            setRexErr(true);
        }
        setRexBusy(false);
    };

    useEffect(() => { trackEvent('landing_view'); }, []);

    const fmtEur = (eur) => formatPrice(eur, currency);
    const planText = (p) => (lang === 'en' && L.pricing.plans?.[p.tier]) || { tagline: p.tagline, features: p.features };

    return (
        <div className="min-h-screen bg-background">
            {/* Sticky nav */}
            <header className="sticky top-0 z-30 backdrop-blur-md bg-background/85 border-b-2 border-secondary" data-testid="landing-header">
                <div className="container-page flex items-center justify-between h-16">
                    <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
                        <img
                            src="/brand/african-valley-logo-256.png"
                            alt="African Valley 360°"
                            className="h-12 w-12 rounded-lg object-contain ring-1 ring-border shadow-soft"
                            data-testid="brand-logo"
                        />
                        <span className="flex items-baseline gap-2">
                            <span className="font-heading text-2xl font-semibold tracking-tight hidden sm:inline">African</span>
                            <span className="font-heading text-2xl text-primary font-medium hidden sm:inline">Valley</span>
                            <span className="font-heading text-sm uppercase tracking-[0.18em] text-foreground/50 ml-1.5 hidden lg:inline">360°</span>
                        </span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-7 text-sm text-foreground/80">
                        <a href="#benefits" className="hover:text-primary transition-colors" data-testid="nav-benefits">{L.nav.benefits}</a>
                        <a href="#solutions" className="hover:text-primary transition-colors">{L.nav.solutions}</a>
                        <a href="#sectors" className="hover:text-primary transition-colors">{L.nav.sectors}</a>
                        <a href="#rex" className="hover:text-primary transition-colors" data-testid="nav-rex">{L.nav.rex}</a>
                        <Link to="/p/campus" className="hover:text-primary transition-colors inline-flex items-center gap-1" data-testid="landing-nav-campus">
                            {L.nav.campus}
                        </Link>
                        <Link to="/p/tarifs" className="hover:text-primary transition-colors" data-testid="nav-tarifs">{L.nav.pricing}</Link>
                    </nav>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <LangSwitcher />
                        <Link to="/auth/signin" data-testid="header-signin" className="hidden sm:block">
                            <Button variant="ghost" size="sm">{lang === 'en' ? 'Sign in' : 'Se connecter'}</Button>
                        </Link>
                        <Link to="/signup-free" data-testid="header-signup">
                            <Button size="sm" className="bg-primary hover:bg-primary-hover">{L.nav.signup}</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* HERO */}
            <section className="relative isolate overflow-hidden min-h-[680px] lg:min-h-[760px] flex items-center" style={{ backgroundColor: '#0E0C0B' }}>
                <div
                    className="absolute inset-0 z-0"
                    style={{ backgroundImage: `url("${IMG_HERO}")`, backgroundSize: 'cover', backgroundPosition: 'center center' }}
                />
                <div
                    className="absolute inset-0 z-10 pointer-events-none"
                    style={{
                        backgroundImage:
                            'linear-gradient(265deg, rgba(14,12,11,0.62) 0%, rgba(14,12,11,0.35) 35%, rgba(14,12,11,0.05) 65%, rgba(14,12,11,0.00) 100%)',
                    }}
                />
                <div
                    className="absolute inset-x-0 bottom-0 h-40 z-10 pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(180deg, rgba(14,12,11,0) 0%, rgba(14,12,11,0.55) 100%)' }}
                />

                <div className="container-page relative z-20 w-full py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="hidden lg:block lg:col-span-5" aria-hidden="true" />
                    <div className="lg:col-span-7 lg:col-start-6 text-background min-w-0">
                        <div className="rounded-2xl bg-black/55 backdrop-blur-md ring-1 ring-white/15 shadow-2xl shadow-black/40 p-7 lg:p-9 animate-fade-up">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-primary mb-3" data-testid="hero-eyebrow">
                                {L.hero.eyebrow}
                            </div>
                            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.12] text-background" data-testid="hero-title">
                                {L.hero.title1}<br />
                                {L.hero.title2}<br />
                                <span className="text-primary">{L.hero.title3}</span>
                            </h1>
                            <p className="mt-5 text-base text-background/90 leading-[1.7] max-w-xl" data-testid="hero-subtitle">
                                {L.hero.sub}
                            </p>
                            <ul className="mt-5 space-y-1.5" data-testid="hero-promises">
                                {L.hero.promises.map((p, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-background/85">
                                        <Check size={15} className="text-primary mt-0.5 flex-shrink-0" /> {p}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-5 text-sm text-background/70 leading-[1.7] max-w-xl" data-testid="hero-free-note">
                                {L.hero.freeNote1}<strong className="text-background">Explorer</strong>{L.hero.freeNote2}
                                <strong className="text-background">{L.hero.freeNoteStrong}</strong>{L.hero.freeNote3}
                            </p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <Link to="/signup-free" className="w-full sm:w-auto">
                                    <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary-hover h-12 px-7 group shadow-lg shadow-black/40" data-testid="hero-cta-primary">
                                        {L.hero.ctaPrimary} <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
                                    </Button>
                                </Link>
                                <Link to="/apply?source=demo">
                                    <Button size="lg" variant="outline" className="h-12 px-7 bg-white/10 backdrop-blur border-white/40 text-background hover:bg-white/20 hover:text-background" data-testid="hero-cta-demo">
                                        {L.hero.ctaDemo}
                                    </Button>
                                </Link>
                            </div>
                            <Link to="/p/project-score" className="mt-4 inline-flex items-center gap-1.5 text-sm text-background/80 hover:text-background underline underline-offset-4 decoration-primary/60" data-testid="hero-cta-score">
                                <Sparkles size={13} className="text-primary" /> {L.hero.ctaScore}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUSTED LOGOS */}
            <TrustedLogosMarquee />

            {/* BENEFITS */}
            <section id="benefits" className="py-32 lg:py-40">
                <div className="container-page">
                    <div className="max-w-3xl mb-20">
                        <div className="eyebrow mb-5 text-primary">{L.benefits.eyebrow}</div>
                        <h2 className="heading-section">{L.benefits.title1}<span className="text-primary">{L.benefits.title2}</span></h2>
                        <p className="text-foreground/70 mt-7 text-lg leading-[1.8] max-w-2xl">{L.benefits.sub}</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="benefits-grid">
                        {L.benefits.items.map(({ title, module, sub }, i) => {
                            const Icon = BENEFIT_ICONS[i];
                            return (
                                <div key={i} className="card-soft p-9 group" data-testid={`benefit-card-${i}`}>
                                    <div className="h-12 w-12 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center mb-6 group-hover:bg-secondary group-hover:text-background transition-colors">
                                        <Icon size={22} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-heading text-xl mb-2">{title}</h3>
                                    <div className="text-[11px] uppercase tracking-wider text-primary font-semibold mb-3">→ {module}</div>
                                    <p className="text-foreground/70 leading-[1.7] text-sm">{sub}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PAIN POINTS */}
            <section id="pains" className="py-32 lg:py-40 bg-surface-muted/30 border-y border-border">
                <div className="container-page">
                    <div className="max-w-3xl mb-20">
                        <div className="eyebrow mb-5 text-primary">{L.pains.eyebrow}</div>
                        <h2 className="heading-section">{L.pains.title1}<span className="text-primary">{L.pains.title2}</span></h2>
                        <div className="mt-7 space-y-5 text-foreground/75 text-lg leading-[1.8] max-w-2xl">
                            <p>{L.pains.p1}</p>
                            <p><strong className="text-foreground">{L.pains.p2strong}</strong>{L.pains.p2}</p>
                            <p>{L.pains.p3a}<strong className="text-foreground">AFRICAN&nbsp;VALLEY&nbsp;360°</strong>{L.pains.p3b}</p>
                            <div className="pt-3">
                                <Link to="/p/diagnostic"
                                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary-hover font-medium text-base transition-all hover:translate-x-0.5"
                                      data-testid="landing-pains-diagnostic-cta">
                                    {L.pains.diagCta} <span aria-hidden="true">→</span>
                                </Link>
                                <span className="block text-xs text-muted-foreground italic mt-2">{L.pains.diagNote}</span>
                            </div>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {L.pains.items.map(({ title, sub }, i) => {
                            const Icon = PAIN_ICONS[i];
                            return (
                                <div key={i} className="card-soft p-8 border-l-4 border-l-destructive/40">
                                    <Icon size={24} strokeWidth={1.6} className="text-destructive mb-4" />
                                    <h3 className="font-heading text-xl mb-3">{title}</h3>
                                    <p className="text-sm text-foreground/65 leading-[1.7]">{sub}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* INNOVATE banner */}
            <section className="relative overflow-hidden">
                <div className="grid lg:grid-cols-2 items-stretch min-h-[560px]">
                    <div className="relative">
                        <img src={IMG_WORKSPACE} alt="African Valley co-creation workspace" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="bg-foreground text-background p-12 lg:p-20 flex flex-col justify-center">
                        <div className="eyebrow text-primary mb-5">{L.innovate.eyebrow}</div>
                        <h2 className="font-heading text-4xl lg:text-5xl leading-tight mb-8" data-testid="landing-signature">
                            <em className="text-primary not-italic">
                                {L.innovate.signature.split('\n').map((line, i, arr) => (
                                    <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
                                ))}
                            </em>
                        </h2>
                        <p className="text-background/85 leading-[1.8] text-lg max-w-xl">
                            <strong className="text-background">AFRICAN VALLEY 360°</strong>{L.innovate.p1a}
                            <strong className="text-background">{L.innovate.p1strong}</strong>{L.innovate.p1b}
                        </p>
                        <p className="text-background/80 leading-[1.8] text-base mt-5 max-w-xl">{L.innovate.p2}</p>
                        <ul className="mt-4 space-y-2 text-background/85 max-w-xl">
                            {[['li1a', 'li1b'], ['li2a', 'li2b'], ['li3a', 'li3b'], ['li4a', 'li4b']].map(([a, b], i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="text-primary mt-1.5 h-1 w-1 rounded-full bg-primary flex-shrink-0" />
                                    <span>{L.innovate[a]}<strong className="text-background">{L.innovate[b]}</strong>{lang === 'fr' ? ',' : ''}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="flex flex-wrap gap-4 mt-10">
                            <Link to="/signup-free">
                                <Button size="lg" className="bg-primary hover:bg-primary-hover">
                                    {L.innovate.ctaJoin} <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </Link>
                            <Link to="/p/about">
                                <Button size="lg" variant="outline" className="bg-transparent border-background/30 text-background hover:bg-background/10 hover:text-background">
                                    {L.innovate.ctaAbout}
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* SOLUTIONS */}
            <section id="solutions" className="py-32 lg:py-40 bg-surface-muted">
                <div className="container-page">
                    <div className="max-w-3xl mb-20">
                        <div className="eyebrow mb-5 text-secondary">{L.solutions.eyebrow}</div>
                        <h2 className="heading-section">{L.solutions.title1}<span className="text-primary">{L.solutions.title2}</span></h2>
                        <p className="text-foreground/70 mt-7 text-lg leading-[1.8] max-w-2xl">{L.solutions.sub}</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
                        {L.solutions.items.map(({ title, sub }, i) => {
                            const Icon = SOLUTION_ICONS[i];
                            return (
                                <div key={i} className="card-soft p-9 group" data-testid={`solution-card-${i}`}>
                                    <div className="h-12 w-12 rounded-xl bg-primary-light text-primary flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                        <Icon size={22} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-heading text-xl mb-3">{title}</h3>
                                    <p className="text-foreground/70 leading-[1.7]">{sub}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* AUDIENCES */}
            <section id="audiences" className="py-32 lg:py-40">
                <div className="container-page">
                    <div className="max-w-3xl mb-20">
                        <div className="eyebrow mb-5 text-primary">{L.audiences.eyebrow}</div>
                        <h2 className="heading-section">{L.audiences.title1}<span className="text-primary">{L.audiences.title2}</span>{L.audiences.title3}</h2>
                        <p className="text-foreground/70 mt-7 text-lg leading-[1.8] max-w-2xl">{L.audiences.sub}</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="audiences-grid">
                        {L.audiences.items.map(({ title, plan, desc, cta }, i) => {
                            const Icon = AUDIENCE_ICONS[i];
                            return (
                                <div key={i} className="card-soft p-8 flex flex-col" data-testid={`audience-card-${i}`}>
                                    <div className="h-12 w-12 rounded-xl bg-primary-light text-primary flex items-center justify-center mb-6">
                                        <Icon size={22} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-heading text-lg mb-2 leading-snug">{title}</h3>
                                    <div className="text-[11px] uppercase tracking-wider text-secondary font-semibold mb-3">{plan}</div>
                                    <p className="text-sm text-foreground/70 leading-[1.7] flex-1">{desc}</p>
                                    <Link to={AUDIENCE_TO[i]} className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid={`audience-cta-${i}`}>
                                        {cta} <ArrowRight size={14} />
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* AI */}
            <section className="py-32 lg:py-40 bg-surface-muted/30 border-y border-border">
                <div className="container-page grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="eyebrow mb-5 text-primary">{L.ai.eyebrow}</div>
                        <h2 className="heading-section">{L.ai.title1}<span className="text-primary">{L.ai.titleAccent}</span>{L.ai.title2}</h2>
                        <p className="text-foreground/75 mt-7 text-lg leading-[1.8]">
                            {L.ai.p1a}<strong>{L.ai.p1strong}</strong>{L.ai.p1b}
                        </p>
                        <p className="text-foreground/75 mt-4 text-lg leading-[1.8]">
                            {L.ai.p2a}<strong>{L.ai.p2strong}</strong>{L.ai.p2b}
                        </p>
                        <p className="text-foreground/75 mt-4 text-lg leading-[1.8] italic">
                            {L.ai.p3a}<strong>{L.ai.p3strong}</strong>{L.ai.p3b}
                        </p>
                        <ul className="mt-10 space-y-4 text-foreground/85 leading-[1.7]">
                            {L.ai.bullets.map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <Sparkles size={16} className="text-secondary mt-1 flex-shrink-0" /> <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="relative">
                        <img src={IMG_AI} alt="VALLEY AI" className="rounded-2xl shadow-soft w-full object-cover h-[520px]" />
                    </div>
                </div>
            </section>

            {/* SECTORS */}
            <section id="sectors" className="py-32 lg:py-40">
                <div className="container-page">
                    <div className="max-w-3xl mb-14">
                        <div className="eyebrow mb-5 text-primary">{L.sectors.eyebrow}</div>
                        <h2 className="heading-section">{L.sectors.title1}<span className="text-primary">{L.sectors.title2}</span>{L.sectors.title3}</h2>
                        <p className="text-foreground/75 mt-7 text-lg leading-[1.8]">
                            {L.sectors.sub1}<strong>AFRICAN VALLEY 360°</strong>{L.sectors.sub2}<strong>{L.sectors.sub2strong}</strong>{L.sectors.sub3}
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2.5 mb-12">
                        {SECTORS_ALL.map((s) => (
                            <span key={s.key}
                                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-border bg-background text-sm hover:border-primary/40 transition-colors"
                                data-testid={`sector-chip-${s.key}`}
                            >
                                <s.icon size={14} className="text-primary" />
                                {s.name}
                            </span>
                        ))}
                    </div>

                    <div className="card-soft p-8 lg:p-10 bg-background border-secondary/20">
                        <div className="flex items-baseline justify-between gap-6 flex-wrap mb-8">
                            <div>
                                <div className="eyebrow mb-3 text-secondary">{L.sectors.foundersEyebrow}</div>
                                <h3 className="font-heading text-2xl lg:text-3xl">{L.sectors.foundersTitle}</h3>
                            </div>
                            <Link to="/p/secteurs" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="sectors-see-more">
                                {L.sectors.seeMore} <ArrowRight size={14} />
                            </Link>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5 text-[15px] leading-[1.7]">
                            {SECTORS_ALL.map((s) => (
                                <div key={s.key} className="flex items-start gap-3">
                                    <span className="text-2xl mt-0.5 flex-shrink-0" aria-hidden="true">{s.emoji || ''}</span>
                                    <div>
                                        <div className="font-medium text-foreground">{s.name}</div>
                                        <div className="text-foreground/70 text-sm leading-[1.6]">{s.pitch}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-8 pt-6 border-t border-border text-sm text-foreground/65 italic leading-[1.7]">
                            {L.sectors.footnote1}<strong>{L.sectors.footnoteStrong}</strong>{L.sectors.footnote2}
                        </p>
                    </div>
                </div>
            </section>

            {/* REX PREMIUM */}
            <section id="rex" className="py-32 lg:py-40 border-y border-border" data-testid="section-rex">
                <div className="container-page grid lg:grid-cols-12 gap-14 items-center">
                    <div className="lg:col-span-6">
                        <div className="eyebrow mb-5 text-primary">{L.rex.eyebrow}</div>
                        <h2 className="heading-section">{L.rex.title1}<span className="text-primary">{L.rex.title2}</span></h2>
                        <p className="text-foreground/70 mt-7 text-lg leading-[1.8]">{L.rex.sub}</p>
                        <ul className="mt-9 space-y-5">
                            {L.rex.points.map((p, i) => (
                                <li key={i} className="flex items-start gap-4" data-testid={`rex-point-${i}`}>
                                    <span className="h-7 w-7 rounded-full bg-secondary-light text-secondary flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Check size={14} />
                                    </span>
                                    <div>
                                        <div className="font-medium text-foreground">{p.title}</div>
                                        <div className="text-sm text-foreground/65 mt-0.5 leading-[1.7]">{p.sub}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <Link to="/signup-free" className="w-full sm:w-auto">
                                <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary-hover h-12 px-7 group" data-testid="rex-cta">
                                    {L.rex.cta} <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
                                </Button>
                            </Link>
                            <span className="text-sm text-foreground/60 italic">{L.rex.ctaNote}</span>
                        </div>
                    </div>
                    <div className="lg:col-span-6 relative min-w-0" data-testid="rex-mockup">
                        {/* Floating testimonial badge */}
                        <div className="absolute -top-5 right-2 sm:right-6 z-10 rotate-2 rounded-xl bg-foreground text-background text-sm px-4 py-2.5 shadow-xl">
                            {L.rex.mock.badge}
                            <span className="block text-[11px] text-background/60 mt-0.5">Grâce K. · {L.rex.mock.status.split('·')[0]}</span>
                        </div>
                        {/* Document mockup */}
                        <div className="rounded-2xl bg-white border border-border shadow-2xl shadow-black/10 -rotate-1 overflow-hidden max-w-xl mx-auto">
                            <div className="h-2 bg-primary" />
                            <div className="p-7 sm:p-9">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-medium">{L.rex.mock.docTitle}</div>
                                        <div className="font-heading text-xl sm:text-2xl mt-1.5 text-foreground">{L.rex.mock.project}</div>
                                        <div className="text-xs text-muted-foreground mt-1">{L.rex.mock.status}</div>
                                    </div>
                                    <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-light text-secondary text-[11px] font-medium">
                                        <ShieldCheck size={12} /> REX
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-3 mt-6">
                                    {L.rex.mock.stats.map((s, i) => (
                                        <div key={i} className="rounded-lg bg-surface-muted/70 px-3 py-3 text-center">
                                            <div className="font-heading text-lg sm:text-xl text-foreground tabular-nums">{s.value}</div>
                                            <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{s.label}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-6 space-y-2.5">
                                    {L.rex.mock.sections.map((s, i) => (
                                        <div key={i} className="flex items-center gap-3 rounded-lg border border-border px-3.5 py-2.5">
                                            <span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
                                            <span className="text-sm text-foreground/85">{s}</span>
                                            <span className="ml-auto flex gap-1">
                                                <span className="h-1 w-8 rounded bg-border" />
                                                <span className="h-1 w-4 rounded bg-border" />
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-[11px] text-muted-foreground">
                                    <span className="inline-flex items-center gap-1.5"><FileStack size={12} /> {L.rex.mock.footer}</span>
                                    <span className="inline-flex items-center gap-1.5 text-primary"><Sparkles size={12} /> Valley AI</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Lead magnet — modèle REX téléchargeable */}
                    <div className="lg:col-span-12">
                        <div className="rounded-2xl border border-primary/25 bg-primary-light/25 p-7 lg:p-9" data-testid="rex-lead-card">
                            <div className="grid lg:grid-cols-12 gap-6 items-center">
                                <div className="lg:col-span-5">
                                    <div className="font-heading text-xl lg:text-2xl">{L.rex.lead.title}</div>
                                    <p className="text-sm text-foreground/65 mt-2 leading-[1.7]">{L.rex.lead.sub}</p>
                                </div>
                                <div className="lg:col-span-7">
                                    {rexDone ? (
                                        <div className="flex items-start gap-3 text-secondary" data-testid="rex-lead-success">
                                            <Check size={20} className="mt-0.5 flex-shrink-0" />
                                            <p className="text-sm leading-[1.7]">
                                                <strong>{L.rex.lead.ok}</strong><br />
                                                {L.rex.lead.okNote}{' '}
                                                <a href="/docs/Modele-REX-African-Valley.pdf" target="_blank" rel="noreferrer" className="underline" data-testid="rex-lead-reopen">{L.rex.lead.okLink}</a>.
                                            </p>
                                        </div>
                                    ) : (
                                        <form onSubmit={submitRexLead} className="flex flex-col sm:flex-row gap-3" data-testid="rex-lead-form">
                                            <input type="text" required value={rexLead.name}
                                                onChange={(e) => setRexLead(v => ({ ...v, name: e.target.value }))}
                                                placeholder={L.rex.lead.namePh}
                                                className="h-12 flex-1 min-w-0 rounded-md border border-border bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                                                data-testid="rex-lead-name" />
                                            <input type="email" required value={rexLead.email}
                                                onChange={(e) => setRexLead(v => ({ ...v, email: e.target.value }))}
                                                placeholder={L.rex.lead.emailPh}
                                                className="h-12 flex-1 min-w-0 rounded-md border border-border bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                                                data-testid="rex-lead-email" />
                                            <Button type="submit" size="lg" disabled={rexBusy}
                                                className="h-12 px-6 bg-primary hover:bg-primary-hover" data-testid="rex-lead-submit">
                                                {rexBusy ? L.rex.lead.busy : L.rex.lead.cta}
                                            </Button>
                                        </form>
                                    )}
                                    {rexErr && <p className="text-sm text-destructive mt-2" data-testid="rex-lead-error">{L.rex.lead.err}</p>}
                                    {!rexDone && <p className="text-[11px] text-foreground/50 mt-2.5">{L.rex.lead.privacy}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="py-32 lg:py-40 bg-surface-muted">
                <div className="container-page">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                        <div className="eyebrow mb-5">{L.pricing.eyebrow}</div>
                        <h2 className="heading-section">{L.pricing.title1}<span className="text-primary">{L.pricing.title2}</span></h2>
                        <p className="text-foreground/70 mt-7 text-lg leading-[1.8]">
                            {L.pricing.sub1}<strong>Explorer</strong>{L.pricing.sub2}<strong>Institution</strong>{L.pricing.sub3}
                        </p>
                    </div>

                    <div className="flex items-center justify-center gap-2 mb-10" data-testid="pricing-currency-switcher">
                        <span className="text-sm text-muted-foreground mr-2">{L.pricing.display}</span>
                        {CURRENCIES.map(c => (
                            <button
                                key={c.code}
                                onClick={() => setCurrency(c.code)}
                                className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                    currency === c.code
                                        ? 'bg-primary text-primary-foreground border-primary shadow-soft'
                                        : 'bg-background text-foreground/70 border-border hover:border-primary/40'
                                }`}
                                data-testid={`currency-btn-${c.code.toLowerCase()}`}
                                title={c.name}
                            >
                                {c.code} · {c.label}
                            </button>
                        ))}
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5" data-testid="landing-pricing-grid">
                        {PLAN_FALLBACK.map((p) => {
                            const pt = planText(p);
                            return (
                                <div key={p.tier} data-testid={`price-${p.tier}`}
                                    className={`card-soft p-6 flex flex-col relative bg-background ${p.recommended ? 'border-primary border-2 shadow-soft' : ''}`}>
                                    {p.recommended && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.18em] font-bold whitespace-nowrap">
                                            {L.pricing.recommended}
                                        </div>
                                    )}
                                    <div className="flex items-baseline gap-2 mb-2">
                                        <span className="text-xl">{p.emoji}</span>
                                        <span className="font-heading text-xl">{p.name}</span>
                                    </div>
                                    <div className="flex items-baseline gap-1.5 mb-1">
                                        <div className="font-heading text-2xl">
                                            {p.contact_only ? L.pricing.quote : p.monthly_eur ? fmtEur(p.monthly_eur) : L.pricing.free}
                                        </div>
                                        {p.monthly_eur ? <div className="text-sm text-muted-foreground">{L.pricing.perMonth}</div> : null}
                                    </div>
                                    {p.yearly_eur ? (
                                        <div className="text-xs text-secondary font-medium mb-3">{L.pricing.yearly(fmtEur(p.yearly_eur))}</div>
                                    ) : p.tier === 'free' ? (
                                        <div className="text-xs text-secondary font-medium mb-3">{L.pricing.freeNote}</div>
                                    ) : <div className="mb-3" />}
                                    <div className="text-xs text-foreground/60 mb-4 leading-[1.6]">{pt.tagline}</div>
                                    <ul className="space-y-1.5 text-[13px] mb-6 flex-1">
                                        {pt.features.slice(0, 4).map((f, i) => (
                                            <li key={i} className="flex items-start gap-2 text-foreground/75">
                                                <Check size={13} className="text-secondary mt-0.5 flex-shrink-0" /> <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Link to={p.tier === 'free' ? '/signup-free' : p.contact_only ? '/p/contact' : '/p/tarifs'}>
                                        <Button className={`w-full ${p.recommended ? 'bg-primary hover:bg-primary-hover' : 'bg-foreground hover:bg-foreground/90 text-background'}`}
                                            data-testid={`price-cta-${p.tier}`}>
                                            {p.tier === 'free' ? L.pricing.ctaFree : p.contact_only ? L.pricing.ctaContact : L.pricing.ctaSee}
                                        </Button>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-14 max-w-3xl mx-auto text-center text-sm text-muted-foreground bg-background border border-border rounded-2xl p-6 leading-[1.7]">
                        {L.pricing.goodToKnow}
                    </div>
                    <div className="mt-6 text-center">
                        <Link to="/p/tarifs" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="pricing-compare-link">
                            {L.pricing.compare} <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section id="testimonials" className="relative py-32 lg:py-40 overflow-hidden">
                <img src={IMG_INSPIRER} alt="" aria-hidden="true"
                    className="absolute inset-y-0 right-0 w-1/2 object-cover opacity-15 hidden lg:block" />
                <div className="container-page relative">
                    <div className="max-w-3xl mb-20">
                        <div className="eyebrow mb-5 text-primary">{L.testimonials.eyebrow}</div>
                        <h2 className="heading-section">{L.testimonials.title1}<span className="text-primary">{L.testimonials.title2}</span></h2>
                        <p className="text-foreground/70 mt-7 text-lg leading-[1.8] max-w-2xl">{L.testimonials.sub}</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-7">
                        {L.testimonials.items.map((tm, i) => (
                            <figure key={i} className={`card-soft p-9 flex flex-col ${tm.highlight ? 'ring-2 ring-primary/25 border-primary/30 bg-primary-light/20' : ''}`} data-testid={`testimonial-${i}`}>
                                <Quote size={28} strokeWidth={1.4} className="text-primary mb-5" />
                                <blockquote className="text-foreground/85 leading-[1.7] flex-1 italic">
                                    « {tm.quote} »
                                </blockquote>
                                <figcaption className="mt-7 pt-5 border-t border-border">
                                    <div className="font-medium">{tm.author}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{tm.role}</div>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                    <div className="mt-12 text-center">
                        <Link to="/p/testimonials" className="text-primary hover:underline text-sm">
                            {L.testimonials.all}
                        </Link>
                    </div>
                </div>
            </section>

            {/* COLLABORATION */}
            <section className="relative overflow-hidden">
                <div className="grid lg:grid-cols-2 items-stretch min-h-[520px]">
                    <div className="bg-secondary text-background p-12 lg:p-20 flex flex-col justify-center order-2 lg:order-1">
                        <div className="eyebrow text-background/70 mb-5">{L.collab.eyebrow}</div>
                        <h2 className="font-heading text-4xl lg:text-5xl leading-tight mb-7">
                            {L.collab.title1}<span className="text-background/95">{L.collab.titleAccent}</span>{L.collab.title2}
                        </h2>
                        <p className="text-background/80 leading-[1.8] text-lg mb-8 max-w-xl">{L.collab.p}</p>
                        <ul className="space-y-3.5 text-background/90 leading-[1.7]">
                            {L.collab.bullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-3"><Check size={16} className="mt-1 flex-shrink-0" /> {b}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="relative order-1 lg:order-2">
                        <img src={IMG_ATRIUM} alt="African Valley" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                </div>
            </section>

            {/* CTA BAND */}
            <section className="relative overflow-hidden py-28 lg:py-36">
                <div
                    className="absolute inset-0 -z-20"
                    style={{
                        backgroundImage: 'url("/brand/cta-event.png")',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center 28%',
                    }}
                    aria-hidden="true"
                />
                <div
                    className="absolute inset-0 -z-10 pointer-events-none"
                    style={{
                        backgroundImage:
                            'linear-gradient(180deg, rgba(14,12,11,0.78) 0%, rgba(14,12,11,0.62) 45%, rgba(14,12,11,0.85) 100%), radial-gradient(circle at 50% 35%, rgba(212,114,47,0.22), transparent 55%)',
                    }}
                    aria-hidden="true"
                />

                <div className="container-page text-center text-background relative">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 backdrop-blur-sm ring-1 ring-primary/40 mb-7" data-testid="cta-band-eyebrow">
                        <Sparkles size={14} className="text-primary" />
                        <span className="text-[11px] uppercase tracking-[0.22em] text-primary font-medium">{L.cta.eyebrow}</span>
                    </div>
                    <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.05] max-w-4xl mx-auto drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
                        {L.cta.title1}<span className="text-primary">{L.cta.titleAccent}</span>{L.cta.title2}
                    </h2>
                    <p className="mt-8 text-lg lg:text-xl text-background/90 max-w-2xl mx-auto leading-[1.8] drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                        {L.cta.sub1}<strong className="text-background">African Valley 360°</strong>{L.cta.sub2}
                    </p>
                    <div className="mt-12 flex flex-wrap gap-4 justify-center">
                        <Link to="/signup-free">
                            <Button size="lg" className="bg-primary hover:bg-primary-hover text-white h-12 px-8 shadow-2xl shadow-primary/30 group" data-testid="cta-band-primary">
                                {L.cta.primary} <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
                            </Button>
                        </Link>
                        <Link to="/apply?source=demo">
                            <Button size="lg" variant="outline" className="h-12 px-8 bg-white/10 backdrop-blur-md border-white/40 text-background hover:bg-white/20 hover:text-background" data-testid="cta-band-secondary">
                                {L.cta.secondary}
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
            <PWAInstallBanner variant="floating" />
        </div>
    );
}
